/** Direct Visit actions use the same single-result wording as selection actions without exposing execution plumbing. */
import { BulkActionStatus, formatBulkActionMessage, VisitBulkActionId } from '@jms/model';
import { ToastService } from '@workbuddy/ui/services';
import { bulkActionTitle } from '@workbuddy/ui/v2/blocks/entity.board/model';
import { VisitMoveResult } from '../visit.movement.types';

/** Command factories call this only after a confirmed result; Pending approvals remain explicitly queued. */
export function reportVisitActionResult(toasts: ToastService, label: string, actionId: VisitBulkActionId, pending = false): void {

  const message = formatBulkActionMessage({
    status: pending ? BulkActionStatus.Pending : BulkActionStatus.Completed,
    total: 1,
    success: pending ? 0 : 1,
    skipped: 0,
    failed: 0,
  }, actionId);
  const title = bulkActionTitle(label);
  if (pending) toasts.info(title, message);
  else toasts.success(title, message);
}

/**
 * A single Visit's move, worded by the shared bulk formatter from the movement operation's totals, with what
 * it refused and what it did to the booking. Partial results are warnings: some of the work stayed where it was.
 * Without a movement (an unchanged appointment confirmed) it is the plain single-result wording.
 */
export function reportVisitMoveResult(toasts: ToastService, label: string, movement: VisitMoveResult | undefined): void {

  if (!movement) {
    reportVisitActionResult(toasts, label, VisitBulkActionId.Reschedule);
    return;
  }
  const title = bulkActionTitle(label);
  const message = visitMoveResultMessage(movement);
  if (movement.rejected.length) toasts.warning(title, message);
  else toasts.success(title, message);
}

/** "1 Visit rescheduled. Moved 3 tasks from 1 Visit into 2 Visits. Booking released." plus each refusal. */
function visitMoveResultMessage(movement: VisitMoveResult): string {

  const counts = movement.counts;
  const parts = [formatBulkActionMessage({
    status: BulkActionStatus.Completed,
    total: 1,
    success: 1,
    skipped: 0,
    failed: 0,
    ...(counts.tasksChanged ? { movement: { tasksMoved: counts.tasksChanged, fromVisits: counts.selectedVisits, resultingVisits: counts.resultingVisits } } : {}),
  }, VisitBulkActionId.Reschedule)];
  if (movement.bookings.moved.length) parts.push('Booking moved with the work.');
  if (movement.bookings.released.length) parts.push('Booking released.');
  for (const refusal of movement.rejected) parts.push(refusal.message);

  return parts.join(' ');
}
